/**
 * KaTeX rendering utility for math expressions.
 * Detects $...$ (inline) and $$...$$ (block) patterns and renders via KaTeX.
 */

import katex from 'katex';

/**
 * Render a LaTeX string to HTML using KaTeX.
 * Returns the HTML string or the original text on error.
 */
export function renderLatex(latex, displayMode = false) {
    try {
        return katex.renderToString(latex, {
            displayMode,
            throwOnError: false,
            strict: false,
        });
    } catch {
        return latex;
    }
}

/**
 * Normalize LaTeX delimiters in raw text before markdown processing.
 * Converts \(...\) → $...$ and \[...\] → $$...$$ so ReactMarkdown
 * doesn't strip the backslashes before our math renderer sees them.
 */
export function normalizeLatexDelimiters(text) {
    if (!text || typeof text !== 'string') return text;
    // Block: \[...\] → $$...$$
    let result = text.replace(/\\\[([\s\S]*?)\\\]/g, (_, inner) => `$$${inner}$$`);
    // Inline: \(...\) → $...$
    result = result.replace(/\\\(([\s\S]*?)\\\)/g, (_, inner) => `$${inner}$`);
    return result;
}

/**
 * Split text into segments of plain text and math expressions.
 * Returns array of { type: 'text'|'inline'|'block', content: string }
 */
export function splitMathSegments(text) {
    if (!text || typeof text !== 'string') return [{ type: 'text', content: text || '' }];

    const segments = [];
    // Match $$...$$ (block) first, then $...$ (inline)
    // Inline: $x$ (single char) or $xy...z$ (multi char, no leading/trailing space)
    const pattern = /(\$\$[\s\S]+?\$\$|\$[^\s$][^$]*?[^\s$]\$|\$[^\s$]\$)/g;

    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(text)) !== null) {
        // Add preceding text
        if (match.index > lastIndex) {
            segments.push({ type: 'text', content: text.slice(lastIndex, match.index) });
        }

        const matched = match[0];
        if (matched.startsWith('$$') && matched.endsWith('$$')) {
            segments.push({ type: 'block', content: matched.slice(2, -2).trim() });
        } else {
            segments.push({ type: 'inline', content: matched.slice(1, -1) });
        }

        lastIndex = match.index + matched.length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
        segments.push({ type: 'text', content: text.slice(lastIndex) });
    }

    return segments.length ? segments : [{ type: 'text', content: text }];
}
